import { Check, DoorClosed, PackageCheck } from 'lucide-preact'
import { text } from '#contract'
import type { LocalizedText, OrderView } from '#contract'
import { useView } from '../../../app/use-view.ts'
import { etaText } from '../../../i18n/eta.ts'
import { useT } from '../../../i18n/index.ts'
import styles from './checkout.module.css'
import tracker from './tracker.module.css'
import { useShop } from './shop-context.ts'

const STEPS = ['shop.step.placed', 'shop.step.onTheWay', 'shop.step.atDoor'] as const

/** "about 1 h 30 min", "Any minute now", "At the door": how far an order is, in a word. */
export function orderStatus(order: OrderView): LocalizedText {
  if (order.state === 'atDoor') return text('shop.step.atDoor')
  if (order.etaMinutes === null) return text('shop.step.onTheWay')
  if (order.etaMinutes <= 15) return text('shop.arrivingSoon')
  return text('shop.eta', { eta: etaText(order.etaMinutes) })
}

/**
 * One order's progress: Placed → On the way → At the door, the ETA on the task list's rule,
 * what's in it, and where to pick it up once there. An order already collected says so.
 */
export function OrderTracker({ id }: { readonly id: number }) {
  const t = useT()
  const { products, money, nav } = useShop()
  const order = useView((view) => view.shops.orders.find((o) => o.id === id) ?? null)
  const keepShopping = (
    <button
      type="button"
      class={styles.secondary}
      onClick={() => {
        nav.reset()
      }}
    >
      {t(text('shop.keepShopping'))}
    </button>
  )
  if (!order) {
    return (
      <div class={styles.empty}>
        <PackageCheck aria-hidden="true" />
        <p>{t(text('shop.collected'))}</p>
        {keepShopping}
      </div>
    )
  }

  const there = order.state === 'atDoor'
  // The step it's at: on the way, or (all done) at the door.
  const at = there ? 2 : 1
  const priced = order.lines.map((line) => {
    const product = products.get(line.item) ?? null
    return { line, product, cents: (product?.priceCents ?? 0) * line.count }
  })
  const fee = order.totalCents - priced.reduce((sum, row) => sum + row.cents, 0)
  return (
    <div class={styles.sheet}>
      <div class={tracker.eta} data-there={there || undefined}>
        <span class={styles.label}>{t(text(there ? 'shop.arrived' : 'shop.arriving'))}</span>
        <span class={tracker.etaValue}>{t(orderStatus(order))}</span>
      </div>
      <div class={styles.box}>
        <ol class={tracker.track}>
          {STEPS.map((step, i) => (
            <li
              key={step}
              class={tracker.trackStep}
              data-state={i < at || there ? 'done' : i === at ? 'now' : 'todo'}
            >
              <span class={tracker.node}>{(i < at || there) && <Check aria-hidden="true" />}</span>
              <span class={tracker.stepName}>{t(text(step))}</span>
            </li>
          ))}
        </ol>
        <p class={tracker.note}>
          <DoorClosed aria-hidden="true" />
          {t(text(there ? 'shop.pickUp' : 'shop.knock'))}
        </p>
      </div>
      <div class={styles.box}>
        <h2 class={styles.boxTitle}>{t(text('shop.order', { id: order.id }))}</h2>
        {priced.map(({ line, product, cents }) => (
          <p key={line.item} class={styles.sum}>
            <span>
              {t(
                text('shop.lineCount', {
                  n: line.count,
                  name: product?.name ?? text('shop.unknownItem'),
                }),
              )}
            </span>
            <span>{product ? money(cents) : ''}</span>
          </p>
        ))}
        <p class={styles.sum}>
          <span>{t(text('shop.fee'))}</span>
          <span>{money(fee)}</span>
        </p>
        <p class={styles.sum} data-total="">
          <span>{t(text('shop.paid'))}</span>
          <span>{money(order.totalCents)}</span>
        </p>
      </div>
      {keepShopping}
    </div>
  )
}
